/**
 * @typedef {import('BMapsModel').MapCase} MapCase
 */
import { App } from 'BMapsSrc/BMapsApp';
import { getTargetInfoMap } from 'BMapsRedux/projectState/access';
import { isGroupEntry } from 'BMapsRedux/projectState/entry';
import { removeSmilesMolName } from 'BMapsUtil/mol_format_utils';
import { floatEquals } from 'BMapsSrc/math';
import { getColumnInfo } from './column_definitions';

export const perProteinColumns = ['bindingScore', 'ligandEfficiency', 'enSolute', 'ligandEfficiencyEn'];
export const linkColumns = ['modType']; // 'fragAtomName'
export const summaryColumns = ['activeTargetCount', 'activeOffTargetCount', 'selectivityScore', 'selectivityRatio'];
export const renderDebug = false;

// Reference (curated panel) members are context only: exclude them from the
// target / off-target secondary-sort tie-breakers below.
const scoredTarget = ({ targetInfo }) => !isGroupEntry(targetInfo) && targetInfo.isTarget;
const scoredOffTarget = ({ targetInfo }) => !isGroupEntry(targetInfo) && !targetInfo.isTarget;

/**
 * Manages information about LigandMod display properties.
 * Currently, these only change when there is a new search.
 * Other state that can change in the context of a single batch of search results
 * (eg search column and which boxes are checked), is in the LigandMod state.
 * This might be a good place to manage which columns are displayed.
 *
 * This class may not be needed anymore.
 */
export class LigandModUIInfo {
    constructor(cmd, hasMultipleFrags=false) {
        const isGrow = cmd === 'grow';

        this.cmd = cmd;
        this.caption = isGrow ? 'candidates for fragment growing' : 'nearby fragments';
        this.displayLinkType = isGrow;
        this.usingSlider = hasMultipleFrags;
        this.addButtonTitle = '';

        if (isGrow) {
            this.addButtonTitle = 'Grow with this fragment';
        } else {
            this.addButtonTitle = 'Add this fragment';
        }

        if (hasMultipleFrags) {
            this.addButtonTitle += ' in the highlighted pose';
        }
    }
}

/**
 * Return a comparator for sorting an array of {@link AnnotatedSearchResult}s.
 * @param {string} sortCol - Column key to sort by, or 'default' to use defaultSortField.
 * @param {boolean} sortAscending - True for ascending order (least-negative first).
 * @param {string} [defaultSortField='enSolute'] - Field used when sortCol is 'default'.
 *   Should match the current primaryScoreField in LigandMod state.
 * @returns {function(AnnotatedSearchResult, AnnotatedSearchResult): number}
 */
export function sortSearchResults({ sortCol: sortColIn, sortAscending, defaultSortField='enSolute' }) {
    // Link-type display order: all linear/acyclic linkers first, then ring linkers;
    // within each group, ascending by the molecular weight (noted in comments).
    const mtypes = [
        // --- Linear / acyclic linkers (ascending bridge MW) ---
        'Bond', //                                                  ~0  (direct bond)
        'methylene', //                                             ~14  (CH2)
        'amine', //                                                 ~15  (NH)
        'ether', //                                                 ~16  (O)
        'acetylene', 'Acetylene', //                                ~24  (C2; 'Acetylene' is legacy capital alias)
        'ethylene_cis', 'ethylene_trans', //                        ~26  (C2H2)
        'ketone', //                                                ~28  (CO)
        'ethane', //                                                ~28  (C2H4)
        'amine-methylene', 'amine-methylene-rev', //                ~29  (NHCH2)
        'ether-methylene', 'ether-methylene-rev', //                ~30  (OCH2)
        'thioether', //                                             ~32  (S)
        'ketone-methylene', 'ketone-methylene-rev', //              ~42  (COCH2)
        'amide', 'amide-rev', //                                    ~43  (CONH)
        'amideBond', 'amideBond-rev', //                            ~43  (CONH)
        'amideSyn', 'amideSyn-rev', //                              ~43  (CONH)
        'thioether-methylene', 'thioether-methylene-rev', //        ~46  (SCH2)
        'sulfoxide', //                                             ~48  (SO)
        'amide-methylene', 'amide-methylene-rev', //                ~57  (CH2CONH)
        'urea', //                                                  ~58  (NHCONH)
        'carbamate', 'carbamate-rev', //                            ~59  (OCONH)
        'sulfone', //                                               ~64  (SO2)
        'thiourea', //                                              ~74  (NHCSNH)
        'sulfonamide', 'sulfonamide-rev', //                        ~79  (SO2NH)
        // --- Ring linkers (ascending bridge MW) ---
        'triazole-123-14diG', 'triazole-123-14diG-rev', //          ~67  (C2HN3)
        'triazole-124-13diG', 'triazole-124-13diG-rev', //          ~67  (C2HN3)
        'triazole-124-35diG', 'triazole-124-35diG-rev', //          ~67  (C2HN3)
        'oxadiazole-124-35diG', 'oxadiazole-124-35diG-rev', //      ~68  (C2N2O)
        'benzene-14diG', //                                         ~76  (C6H4)
        'benzene-13diG', //                                         ~76  (C6H4)
        'benzene-12diG', //                                         ~76  (C6H4)
        'piperazine', //                                            ~84  (C4H8N2)
        'CXC', //                                                   Legacy
    ];
    // indexOf returns -1 for unknowns, which would sort them *before*
    // recognized types. Clamp to the end instead so any modType added
    // to the rendering layer without being added here still sorts in a
    // stable, intuitive position.
    const mtypeIndex = (t) => {
        const i = mtypes.indexOf(t);
        return i === -1 ? mtypes.length : i;
    };

    return function (aRow, bRow) {
        let sortCol = sortColIn;
        if (sortCol === 'default') sortCol = defaultSortField;

        const [firstCaseA, firstCaseEntryA] = Object.entries(aRow.perCaseInfo)[0];
        const [firstCaseB, firstCaseEntryB] = Object.entries(bRow.perCaseInfo)[0];
        if (firstCaseA !== firstCaseB) {
            console.warn(`LigandMod sort: first projectCases don't match: ${firstCaseA} !== ${firstCaseB}`);
        }

        // Default comparison is just from the search result
        let compA = aRow[sortCol];
        let compB = bRow[sortCol];

        // Special handling for per-protein columns
        const {
            column: col, group, projectCase, properties,
        } = getColumnInfo(sortCol);
        if (group === 'perProteinColumns') {
            const isMissing = !projectCase || !aRow.perCaseInfo[projectCase];
            if (isMissing) {
                if (projectCase) {
                    console.log(`LigandMod sort can't find entry for ${projectCase} for ${col} (${sortCol}), using first case: ${firstCaseA}`);
                }
                compA = firstCaseEntryA?.[col];
                compB = firstCaseEntryB?.[col];
            } else {
                if (properties.getValue) {
                    compA = properties.getValue(aRow, col, projectCase);
                    compB = properties.getValue(bRow, col, projectCase);
                } else {
                    compA = aRow.perCaseInfo[projectCase]?.[col];
                    compB = bRow.perCaseInfo[projectCase]?.[col];
                }
            }
        }

        // Special handling for summary columns
        if (summaryColumns.includes(sortCol)) {
            compA = aRow.summaryInfo[sortCol];
            compB = bRow.summaryInfo[sortCol];

            // secondary sort for summary columns,
            // Switch a and b to compensate for the fact that these columns are positive,
            // but energies are negative.
            if (compA === compB && compA != null) {
                const perCaseA = Object.values(aRow.perCaseInfo);
                const perCaseB = Object.values(bRow.perCaseInfo);
                if (['selectivityScore', 'selectivityRatio', 'activeTargetCount'].includes(sortCol)) {
                    const firstTargetA = perCaseA.find(scoredTarget);
                    const firstTargetB = perCaseB.find(scoredTarget);
                    compA = firstTargetA?.[defaultSortField];
                    compB = firstTargetB?.[defaultSortField];
                    // Switch a and b because of opposite sort order
                    if (compA != null && compB != null) [compA, compB] = [compB, compA];
                } else if (['activeOffTargetCount'].includes(sortCol)) {
                    const firstOffA = perCaseA.find(scoredOffTarget);
                    const firstOffB = perCaseB.find(scoredOffTarget);
                    compA = firstOffA?.[defaultSortField];
                    compB = firstOffB?.[defaultSortField];
                    // Switch a and b because of opposite sort order
                    if (compA != null && compB != null) [compA, compB] = [compB, compA];
                } else {
                    console.warn(`LigandMod sort: no secondary sort defined for summary column ${sortCol}`);
                }
            }
        }

        // Null values always sort to the bottom, regardless of sort direction.
        if (compA != null && compB == null) return -1;
        else if (compA == null && compB != null) return 1;
        else if (compA == null && compB == null) return 0;

        // Special handling for other columns
        if (sortCol === 'modType') {
            // modTypes are sorted by index (not by string value) to preserve order
            compA = mtypeIndex(compA);
            compB = mtypeIndex(compB);
            if (compA === compB) {
                compA = firstCaseEntryA[defaultSortField];
                compB = firstCaseEntryB[defaultSortField];
            }
        } else if (sortCol === 'name') {
            compA = compA.toLowerCase();
            compB = compB.toLowerCase();
            // Secondary sort for name: modType (link type)
            if (compA === compB) {
                // modTypes are sorted by index (not by string value) to preserve order
                compA = mtypeIndex(aRow['modType']);
                compB = mtypeIndex(bRow['modType']);
            }
        }

        if (compA > compB) return sortAscending ? 1 : -1;
        else if (compA < compB) return sortAscending ? -1 : 1;
        else if (compA === compB) return 0;
        else return 0; // This would be unexpected
    };
}

/**
 * Return an object with the properties of the fragment associated with a fragment search result.
 *
 * @param {AnnotatedSearchResult} sugg
 * @param {string[]} projectCases
 * @returns {{
 *      name: string, smiles: string, mwt: number,
 *      charge: number, HAC: number, LogP: number, TPSA: number,
 *      HBA: number, HBD: number, RBC: number,
 *      conflictingProps: object?
 * }}
 */
export function getFragmentPropsForSuggestion(sugg, projectCases) {
    const fragProps = {
        name: sugg.name,
        smiles: null, // filled out below
        mwt: sugg.mwt,
    };

    let conflictingProps = null;

    // Add properties by name.
    // It is possible for the values to be different for the same property name,
    // so collect alternate values in conflictingProps.
    // TODO: investigate and better handle multiple definitions,
    // especially those with same names in basis and minifrags:
    //     pyrazole, imidazole, pyridine, morpholine_Prot
    function addProp(propName, value) {
        if (!value && value !== 0) return;
        const existing = fragProps[propName];
        if (existing == null) {
            fragProps[propName] = value;
        } else if (valueChanged(existing, value)) {
            if (!conflictingProps) conflictingProps = {};
            if (!conflictingProps[propName]) conflictingProps[propName] = [existing];
            if (!conflictingProps[propName].includes(value)) {
                conflictingProps[propName].push(value);
            }
        } // otherwise they are equal, so keep the existing value
    }

    function valueChanged(existing, incoming) {
        if (typeof existing !== typeof incoming) {
            return true;
        } else if (typeof existing === 'number') {
            return !floatEquals(existing, incoming);
        } else {
            return existing !== incoming;
        }
    }

    // Smiles is taken fom the available fragments reported by bfd-server.
    // Because each projectCase reports its own available fragments,
    // there is a chance that the smiles could be different.
    for (const pc of projectCases) {
        const fragInfo = App.Workspace.getInfoForFragment(sugg.name, pc);
        if (fragInfo) {
            const smiles = removeSmilesMolName(fragInfo.smiles);
            addProp('smiles', smiles);
        }
    }

    // All other props are taken from those reported by the Fragment Service fetch
    const fragServFragInfo = App.Workspace.fragmentData.fragservData.fragInfoByFragName(sugg.name);
    const fragPropCols = ['charge', 'HAC', 'LogP', 'TPSA', 'HBA', 'HBD', 'RBC'];
    for (const col of fragPropCols) {
        for (const { fragment: fragmentInfoItem } of fragServFragInfo) {
            // BUG: additional proteins are clearing the molprops from previous proteins.
            // So if frag A is in Protein 1, but not Protein 2, it will be missing props.
            const value = fragmentInfoItem.molProps[col];
            addProp(col, value);
        }
    }
    fragProps.conflictingProps = conflictingProps;
    return fragProps;
}

// Index of projectCase -> { mapCase, targetInfo }, cached against the (memoized) targetInfo map
// reference so it rebuilds only when the target list changes. First entry wins for a given
// projectCase, matching the previous first-match linear scan (incl. the '' key shared by
// scheme imports). Avoids an O(cases) scan per call -- getMapCaseAndTargetInfo is called per
// (suggestion x case) during the fragment-search build.
let cachedTargetMapRef = null;
let cachedInfoByProjectCase = null;

/**
 * Grab the map case and targetinfo for a projectcase
 * @param {string} projectCase
 * @returns {{mapCase: MapCase, targetInfo: TargetInfo}}
 */
export function getMapCaseAndTargetInfo(projectCase) {
    const targetMap = getTargetInfoMap();
    if (targetMap !== cachedTargetMapRef) {
        cachedTargetMapRef = targetMap;
        cachedInfoByProjectCase = new Map();
        for (const [mapCase, targetInfo] of targetMap.entries()) {
            if (!cachedInfoByProjectCase.has(mapCase.projectCase)) {
                cachedInfoByProjectCase.set(mapCase.projectCase, { mapCase, targetInfo });
            }
        }
    }
    return cachedInfoByProjectCase.get(projectCase) ?? {};
}

/**
 *
 * @param {string[]} projectCases
 * @returns {boolean} whether any of the project cases have a kinaseId
 */
export function isKinaseInProjectCases(projectCases) {
    for (const pc of projectCases) {
        const { mapCase } = getMapCaseAndTargetInfo(pc);
        const hasKinase = !!getKinaseIdForMapCase(mapCase);
        if (hasKinase) {
            return true;
        }
    }
    return false;
}

/**
 * Allow pulling kinase id from "other properties" (defined in map-list.json)
 * or from the klifs properties fetched from the klifs API (via integrations inspector tab).
 * @param {import('BMapsModel').MapCase} mapCase
 * @returns {string}
 */
export function getKinaseIdForMapCase(mapCase) {
    if (mapCase.otherProperties?.kinaseId) return mapCase.otherProperties.kinaseId;
    let kinaseId = mapCase.getProperty('klifs', 'kinase');
    if (Array.isArray(kinaseId)) kinaseId = kinaseId[0];
    return kinaseId || '';
}
